import { motion } from "motion/react";

import BrokenSearch from "../../utils/projects/Bono/BrokenSearch.png";
import { Badge } from "../ui/badge";
import {
  CalloutBox,
  EnhancedListItem,
  ProcessStep,
  ProjectHeader,
  ProjectSection,
  StatsGrid,
  TwoColumnLayout,
} from "./Components";
import { ProjectPageLayout } from "./ProjectPageLayout";
import { projectsById } from "../../utils/info";

// Image imports
import BonoBrand1 from "../../utils/projects/Bono/BonoBrand1.png";
import BonoBrand2 from "../../utils/projects/Bono/BonoBrand2.png";
import BonoBrand3 from "../../utils/projects/Bono/BonoBrand3.png";
import BonoBrand4 from "../../utils/projects/Bono/BonoBrand4.png";
import BonoCover from "../../utils/projects/Bono/BonoCover.png";
import LofiChat from "../../utils/projects/Bono/ChatLofi.png";
import LofiDiscover from "../../utils/projects/Bono/DiscoverLofi.png";
import LofiHome from "../../utils/projects/Bono/HomepageLofi.png";
import CompetitorAnalysis from "../../utils/projects/Bono/Competitve.png";
import UserJourney from "../../utils/projects/Bono/UserJourney.png";
import Demo from "../../utils/projects/Bono/ninaVideo.mp4";

export const BonoProjectPage = () => {
  const bono = projectsById.bono;
  return (
    <ProjectPageLayout projectId="bono" headerMaxWidthClassName="max-w-4xl">
      {/* Overview Image */}
      <ProjectSection id="overview">
        <div
          className={`bg-gradient-to-br rounded-3xl p-8 backdrop-blur-sm ${bono?.color}`}
        >
          <motion.img
            src={BonoCover}
            alt="Bono Project Overview"
            className="w-full rounded-2xl shadow-2xl mb-8"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          />
        </div>
      </ProjectSection>

      {/* TL;DR Section */}
      <ProjectSection id="tldr" className="space-y-6">
        <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-2xl p-6 border-2 border-blue-200">
          <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-3">
            TL;DR
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">⏰</span>
                <h4 className="font-bold text-sm">The Problem</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Finding the right charity takes <strong>5-6 hours</strong> of
                research—users feel overwhelmed and often give up
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🤖</span>
                <h4 className="font-bold text-sm">The Solution</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                AI-powered matching cuts research time to{" "}
                <strong>minutes</strong> with personalized charity
                recommendations
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🎯</span>
                <h4 className="font-bold text-sm">My Role</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Led research (42 interviews, 44 surveys), designed brand
                identity, built React prototype
              </p>
            </div>
          </div>
        </div>
      </ProjectSection>

      {/* Problem & Solution Section */}
      <ProjectSection id="about" className="space-y-6">
        <ProjectHeader label="Problem & Solution" />

        <div className="grid md:grid-cols-2 gap-6">
          {/* Problem */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-red-50/80 to-orange-50/80 rounded-2xl p-6 border border-red-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-xl">⚠️</span>
              </div>
              <h3 className="font-bold text-lg">The Problem</h3>
            </div>
            <div className="space-y-3 text-sm leading-relaxed text-gray-700">
              <p>
                <strong>Finding the right charity takes 5-6 hours.</strong> The
                process is broken:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Search paralysis:</strong> Thousands of results with
                    no clear differentiation
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Trust issues:</strong> 3-4 additional hours
                    verifying finances and legitimacy
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Decision fatigue:</strong> Users often give up
                    before donating
                  </span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Solution */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-green-50/80 to-emerald-50/80 rounded-2xl p-6 border border-green-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <span className="text-white text-xl">✅</span>
              </div>
              <h3 className="font-bold text-lg">Our Solution</h3>
            </div>
            <div className="space-y-3 text-sm leading-relaxed text-gray-700">
              <p>
                <strong>Bono</strong> transforms hours into minutes:
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">🤖</span>
                  <span>
                    <strong>AI-powered matching</strong> surfaces relevant
                    charities based on your values
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">🔍</span>
                  <span>
                    <strong>Verified profiles</strong> with pre-vetted financial
                    data and impact metrics
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-green-700">📊</span>
                  <span>
                    <strong>Donation tracking</strong> showing real-time impact
                    of your contributions
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <StatsGrid
          items={[
            {
              label: "User Interviews",
              value: "42",
              color: "text-blue-600",
            },
            {
              label: "Survey Responses",
              value: "44",
              color: "text-blue-600",
            },
            {
              label: "Research Duration",
              value: "6 mo",
              color: "text-blue-600",
            },
          ]}
        />
      </ProjectSection>

      {/* User Research Section */}
      <ProjectSection id="research">
        <ProjectHeader label="User Research" />

        <CalloutBox title="Research Approach">
          <p className=" leading-relaxed mb-4">
            I conducted{" "}
            <strong>
              comprehensive research with 42 interviews and 44 surveys
            </strong>{" "}
            across diverse stakeholders to understand the entire giving
            ecosystem—from college students to nonprofit leaders.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              "College Students",
              "Volunteers",
              "Social Impact Leaders",
              "Financial Educators",
              "Tax Lawyers",
              "Angel Investors",
              "Nonprofits",
              "Community Leaders",
            ].map((participant, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="bg-white/70 text-blue-800 text-xs px-3 py-1.5 text-center"
              >
                {participant}
              </Badge>
            ))}
          </div>
        </CalloutBox>

        {/* Broken Search Visual */}
        <div className="my-12">
          <h3 className="text-2xl font-bold mb-4">
            The "Broken Search" Problem
          </h3>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-red-50/80 to-orange-50/80 rounded-2xl p-6 backdrop-blur-sm border border-red-100"
          >
            <img
              src={BrokenSearch}
              alt="Broken Search Problem Visualization"
              className="w-full rounded-xl shadow-lg mb-4"
            />
            <div className="space-y-3">
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong>The current process is exhausting:</strong> Users spend{" "}
                <strong>5-6 hours</strong> navigating a fragmented journey:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white/70 p-3 rounded-lg">
                  <div className="font-bold text-sm text-red-700 mb-1">
                    Step 1: Search
                  </div>
                  <p className="text-xs text-gray-600">
                    Enter vague query, get thousands of irrelevant results
                  </p>
                </div>
                <div className="bg-white/70 p-3 rounded-lg">
                  <div className="font-bold text-sm text-red-700 mb-1">
                    Step 2: Filter
                  </div>
                  <p className="text-xs text-gray-600">
                    Manually sift through organizations with limited info
                  </p>
                </div>
                <div className="bg-white/70 p-3 rounded-lg">
                  <div className="font-bold text-sm text-red-700 mb-1">
                    Step 3: Verify
                  </div>
                  <p className="text-xs text-gray-600">
                    Spend 3-4 hours researching finances and legitimacy
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* User Journey */}
        <div className="my-12">
          <h3 className="text-2xl font-bold mb-4">Current User Journey</h3>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-50/80 to-purple-50/80 rounded-2xl p-6 backdrop-blur-sm border border-blue-100"
          >
            <img
              src={UserJourney}
              alt="Current User Journey Map"
              className="w-full rounded-xl shadow-lg mb-4"
            />
            <div className="space-y-3">
              <p className="text-sm text-gray-700 leading-relaxed">
                The journey reveals a critical disconnect: users start motivated
                but hit <strong>two major friction points</strong> that often
                lead to abandonment:
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-3 bg-white/70 p-3 rounded-lg">
                  <span className="text-xl">1️⃣</span>
                  <div>
                    <div className="font-bold text-sm mb-1">
                      Decision Point: Which charity?
                    </div>
                    <p className="text-xs text-gray-600">
                      Overwhelmed by options with no clear differentiation—many
                      users stall here
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-white/70 p-3 rounded-lg">
                  <span className="text-xl">2️⃣</span>
                  <div>
                    <div className="font-bold text-sm mb-1">
                      Verification Deep Dive: Is this trustworthy?
                    </div>
                    <p className="text-xs text-gray-600">
                      Another 3-4 hours cross-referencing finances, impact data,
                      and reviews—exhausting due diligence
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Key Insights */}
        <div className="space-y-6">
          <h3 className="text-2xl font-bold mb-6">Key Insights</h3>

          <EnhancedListItem
            index={0}
            title="Motivation Exists, Action Doesn't"
            description="College students are eager to make a difference through donations, but uncertainty about which charities to trust leads to endless research cycles—and ultimately, no action. The 5-6 hour investment feels too risky without guaranteed outcomes."
          />

          <EnhancedListItem
            index={1}
            title="Overwhelmed by Undifferentiated Choice"
            description="Users feel skeptical about the vast number of nonprofit organizations. Too many options without clear differentiation creates decision paralysis rather than empowerment. They need curation, not more options."
          />

          <EnhancedListItem
            index={2}
            title="Trust Requires Transparency"
            description="The 3-4 hour verification phase exists because users can't trust charity websites at face value. They need pre-vetted financial data, impact metrics, and third-party validation—all in one place."
          />
        </div>

        {/* Target Audience */}
        <CalloutBox title="Target Audience Decision">
          <p className="leading-relaxed">
            Initially, our scope was broad: "help people give back." Through
            research, we discovered <strong>college students</strong>{" "}
            represented an underserved yet motivated audience—a group we could
            relate to personally and design for across the spectrum from casual
            to highly engaged donors.
          </p>
        </CalloutBox>

        {/* Personas */}
        <div className="my-12">
          <h3 className="text-2xl font-bold mb-6">User Personas</h3>
          <p className="text-gray-600 mb-6 text-center">
            Two archetypes representing the spectrum of college student donors
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 rounded-2xl p-6 border border-blue-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xl">
                  A
                </div>
                <div>
                  <h4 className="font-bold text-lg">Average Alex</h4>
                  <p className="text-xs text-gray-600">The Curious Newcomer</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-bold text-blue-700">Profile:</span>
                  <p className="text-gray-700 mt-1">
                    College student who has never donated before and hasn't felt
                    compelled to—yet. Open to the idea but needs a clear,
                    low-friction path.
                  </p>
                </div>
                <div>
                  <span className="font-bold text-blue-700">Needs:</span>
                  <p className="text-gray-700 mt-1">
                    Simple discovery, trustworthy recommendations, minimal time
                    investment
                  </p>
                </div>
                <div>
                  <span className="font-bold text-blue-700">Pain Points:</span>
                  <p className="text-gray-700 mt-1">
                    Doesn't know where to start, skeptical of charity
                    authenticity, easily overwhelmed
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-purple-50/80 to-pink-50/80 rounded-2xl p-6 border border-purple-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-xl">
                  P
                </div>
                <div>
                  <h4 className="font-bold text-lg">Passionate Patrick</h4>
                  <p className="text-xs text-gray-600">The Engaged Advocate</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-bold text-purple-700">Profile:</span>
                  <p className="text-gray-700 mt-1">
                    Deeply committed to driving positive change through
                    donations and volunteering. Values alignment is
                    critical—won't donate unless confident in impact.
                  </p>
                </div>
                <div>
                  <span className="font-bold text-purple-700">Needs:</span>
                  <p className="text-gray-700 mt-1">
                    Detailed impact metrics, financial transparency, ability to
                    track donation outcomes
                  </p>
                </div>
                <div>
                  <span className="font-bold text-purple-700">
                    Pain Points:
                  </span>
                  <p className="text-gray-700 mt-1">
                    Frustrated by lack of transparency, difficult to navigate
                    donation processes, hard to verify legitimacy
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </ProjectSection>

      {/* Competitive Analysis */}
      <ProjectSection id="competitive">
        <ProjectHeader label="Competitive Analysis" />

        <CalloutBox title="Market Landscape">
          <p className="leading-relaxed mb-4">
            I analyzed <strong>10 major players</strong> in the charity
            discovery and evaluation space to identify gaps our solution could
            address.
          </p>
        </CalloutBox>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-blue-50/80 to-purple-50/80 rounded-2xl p-6 backdrop-blur-sm mb-6"
        >
          <img
            src={CompetitorAnalysis}
            alt="Competitive Analysis Matrix"
            className="w-full rounded-xl shadow-lg mb-4"
          />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2 text-sm">✅ What They Do Well</h4>
              <ul className="space-y-1 text-xs text-gray-700">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>Benevity:</strong> Workplace giving integration
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>Charity Navigator:</strong> Comprehensive rating
                    system
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>CharityWatch:</strong> Financial transparency focus
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>Candid:</strong> Nonprofit database depth
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2 text-sm">❌ What's Missing</h4>
              <ul className="space-y-1 text-xs text-gray-700">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>No personalization:</strong> Generic lists, not
                    value-based matching
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>Poor UX:</strong> Dense tables and reports, not
                    intuitive interfaces
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>No storytelling:</strong> Numbers without impact
                    narratives
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>No tracking:</strong> One-time donation, no ongoing
                    relationship
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-green-50 rounded-xl border-2 border-green-200">
          <p className="text-sm font-semibold text-green-800">
            <strong>Our Opportunity:</strong> Combine Charity Navigator's trust
            + Benevity's UX + AI personalization + impact tracking—creating a
            comprehensive platform that serves both Average Alex and Passionate
            Patrick.
          </p>
        </div>
      </ProjectSection>

      {/* Brand Identity Section */}
      <ProjectSection id="branding">
        <ProjectHeader label="Brand Identity" />

        <CalloutBox title="Design Philosophy">
          <p className="leading-relaxed">
            I established <strong>brand guidelines early</strong> to ensure
            consistency across the product. The palette of{" "}
            <strong>blues and yellows</strong> was intentional: blues evoke
            trust and calm (critical for financial decisions), while yellows add
            optimism and energy (making giving feel joyful, not transactional).
          </p>
        </CalloutBox>

        <TwoColumnLayout
          left={
            <div>
              <h3 className="text-xl font-bold mb-4">Color Strategy</h3>
              <div className="space-y-3">
                <div className="bg-white/70 p-4 rounded-xl border-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">💙</span>
                    <h4 className="font-bold text-sm">Blue Tones</h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    Trust, reliability, and calmness for financial confidence
                  </p>
                </div>
                <div className="bg-white/70 p-4 rounded-xl border-2 border-yellow-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">💛</span>
                    <h4 className="font-bold text-sm">Yellow Accents</h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    Optimism, happiness, and energy to make giving feel joyful
                  </p>
                </div>
              </div>
            </div>
          }
          right={
            <div>
              <h3 className="text-xl font-bold mb-4">Typography & Tone</h3>
              <div className="space-y-3">
                <div className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">✨</span>
                    <h4 className="font-bold text-sm">Approachable</h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    Friendly typography that feels welcoming, not corporate
                  </p>
                </div>
                <div className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">🎯</span>
                    <h4 className="font-bold text-sm">Simple & Clear</h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    Clean layouts that reduce cognitive load during decisions
                  </p>
                </div>
              </div>
            </div>
          }
        />

        <div className="my-12">
          <h3 className="text-2xl font-bold mb-4">Brand Guidelines</h3>
          <p className="text-gray-600 mb-6 text-center">
            Complete visual system including logo variations, color palette,
            typography hierarchy, and component library
          </p>
          <div className="grid grid-cols-2 gap-6">
            {[
              { img: BonoBrand1, label: "Logo & Wordmark" },
              { img: BonoBrand2, label: "Color System" },
              { img: BonoBrand3, label: "Typography" },
              { img: BonoBrand4, label: "Component Library" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-100/80 to-yellow-100/80 rounded-2xl p-4 backdrop-blur-sm"
              >
                <img
                  src={item.img}
                  alt={item.label}
                  className="w-full rounded-xl shadow-lg mb-3"
                />
                <p className="text-sm font-semibold text-center text-gray-700">
                  {item.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </ProjectSection>

      {/* Design Process Section */}
      <ProjectSection id="process">
        <ProjectHeader label="Design Process" />

        <p className="text-gray-600 mb-8 text-center">
          From whiteboard ideation to React prototype, iterating based on user
          feedback throughout
        </p>

        <div className="space-y-4">
          <ProcessStep
            number="1"
            title="Ideation & Concept Exploration"
            description="Used whiteboard exercises and sticky notes to explore multiple approaches: gamification for habit-building, bite-sized educational content, AI storytelling for emotional connection, and browser plugins for seamless giving."
            color="bg-blue-100"
          />
          <ProcessStep
            number="2"
            title="Low-Fidelity Wireframing"
            description="Created initial wireframes in Figma to test information architecture and user flows. Focused on three core experiences: discovery (finding charities), profiles (building trust), and tracking (showing impact)."
            color="bg-blue-100"
          />
          <ProcessStep
            number="3"
            title="High-Fidelity Design & Testing"
            description="Designed and refined individual components while maintaining consistency across the platform. Tested prototypes with college students to validate UI decisions and interaction patterns."
            color="bg-blue-100"
          />
        </div>

        <div className="my-12">
          <h3 className="text-2xl font-bold mb-4">Low-Fidelity Wireframes</h3>
          <p className="text-gray-600 mb-6 text-center">
            Early concepts testing the core user flows and information
            architecture
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                image: LofiHome,
                title: "Homepage",
                description:
                  "Dashboard with personalized charity recommendations and quick actions",
              },
              {
                image: LofiDiscover,
                title: "Discover",
                description:
                  "Browse interface with filters by cause, location, and impact area",
              },
              {
                image: LofiChat,
                title: "AI Chat",
                description:
                  "Conversational interface to understand user values and suggest matches",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-100/80 to-yellow-100/80 rounded-2xl p-6 backdrop-blur-sm"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full rounded-xl shadow-lg mb-4 max-h-[350px] object-contain"
                />
                <h4 className="font-bold mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </ProjectSection>

      {/* Final Product Section */}
      <ProjectSection id="final">
        <ProjectHeader label="Final Product" />

        <CalloutBox title="Design Mission">
          <p className="leading-relaxed">
            Bono empowers users to share their values and passions, connecting
            them to causes where their contribution creates meaningful
            change—not just another transaction. By eliminating the{" "}
            <strong>5-6 hour research burden</strong>, we enable action over
            analysis paralysis.
          </p>
        </CalloutBox>

        <div className="my-12">
          <h3 className="text-2xl font-bold mb-6">Key Features</h3>

          <div className="space-y-6">
            <EnhancedListItem
              index={0}
              title="AI-Powered Charity Matching"
              description="Conversational AI chatbox helps users discover charities aligned with their values. Share your interests, causes you care about, and giving preferences—Bono instantly surfaces personalized matches, eliminating hours of research."
            />

            <EnhancedListItem
              index={1}
              title="Transparent Organization Profiles"
              description="Comprehensive charity profiles with verified information: mission statements, financial breakdowns, impact metrics, and real stories from beneficiaries. Build trust through transparency, not just ratings—addressing the 3-4 hour verification phase."
            />

            <EnhancedListItem
              index={2}
              title="Impact Tracking Dashboard"
              description="See exactly where your donations go and what change they create. Real-time updates showing tangible outcomes—from meals provided to scholarships funded—making the impact of giving visible and motivating."
            />
          </div>
        </div>

        <TwoColumnLayout
          left={
            <div>
              <h3 className="text-xl font-bold mb-4">Implementation</h3>
              <p className="text-sm leading-relaxed mb-4">
                Built with <strong>React</strong> for a responsive, modern web
                experience. AI integration for personalized recommendations,
                with focus on performance and accessibility.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs px-3 py-1">
                  React
                </Badge>
                <Badge variant="secondary" className="text-xs px-3 py-1">
                  Responsive Design
                </Badge>
                <Badge variant="secondary" className="text-xs px-3 py-1">
                  AI/ML Integration
                </Badge>
                <Badge variant="secondary" className="text-xs px-3 py-1">
                  RESTful APIs
                </Badge>
              </div>
            </div>
          }
          right={
            <div>
              <h3 className="text-xl font-bold mb-4">Design Principles</h3>
              <div className="space-y-3">
                <div className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">🎯</span>
                    <h4 className="font-bold text-sm">Personalization</h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    AI-curated matches based on values, not demographics
                  </p>
                </div>
                <div className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">🔒</span>
                    <h4 className="font-bold text-sm">
                      Trust Through Transparency
                    </h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    Pre-vetted financial data eliminates 3-4 hour verification
                  </p>
                </div>
                <div className="bg-white/70 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">📊</span>
                    <h4 className="font-bold text-sm">Visible Impact</h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    Real-time tracking showing tangible outcomes
                  </p>
                </div>
              </div>
            </div>
          }
        />
      </ProjectSection>

      <ProjectSection id="demo">
        <ProjectHeader label="Product Demo" />

        <CalloutBox title="Experience Bono in Action">
          <p className="leading-relaxed">
            Watch how Bono transforms the{" "}
            <strong>5-6 hour charity search</strong> into a{" "}
            <strong>personalized, guided experience</strong>. This demo
            showcases our AI-powered matching, transparent organization
            profiles, and impact tracking—all designed to help users move from
            curiosity to confident giving in minutes, not hours.
          </p>
        </CalloutBox>

        <div className="my-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-100/80 to-yellow-100/80 rounded-2xl p-6 backdrop-blur-sm"
          >
            <video
              src={Demo}
              className="w-full rounded-xl shadow-lg max-h-[500px] object-contain"
              controls
              poster={BonoCover} // Optional: add a poster image
            />
            <p className="text-sm text-center text-gray-600 mt-4">
              Nina discovers charities aligned with her environmental values
              using Bono's AI chat
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white/70 p-4 rounded-xl text-center">
            <div className="text-2xl mb-2">🤖</div>
            <h4 className="font-bold text-sm mb-1">AI Matching</h4>
            <p className="text-xs text-gray-600">
              Personalized recommendations based on values
            </p>
          </div>
          <div className="bg-white/70 p-4 rounded-xl text-center">
            <div className="text-2xl mb-2">🔍</div>
            <h4 className="font-bold text-sm mb-1">Verified Profiles</h4>
            <p className="text-xs text-gray-600">
              Transparent financial data and impact metrics
            </p>
          </div>
          <div className="bg-white/70 p-4 rounded-xl text-center">
            <div className="text-2xl mb-2">📊</div>
            <h4 className="font-bold text-sm mb-1">Impact Tracking</h4>
            <p className="text-xs text-gray-600">
              Real-time updates on donation outcomes
            </p>
          </div>
        </div>
      </ProjectSection>

      {/* Reflection Section */}
      <ProjectSection id="reflection">
        <ProjectHeader label="Reflection & Learnings" />

        <CalloutBox title="What I Learned">
          <div className="space-y-4">
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">⏰ Quantify the Problem</h4>
              <p className="text-sm leading-relaxed">
                Discovering the <strong>5-6 hour research burden</strong> wasn't
                in our initial assumptions—it emerged through user journey
                mapping. This specific, quantifiable pain point made the value
                proposition crystal clear and guided every design decision.
              </p>
            </div>
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">👥 Design for the Spectrum</h4>
              <p className="text-sm leading-relaxed">
                Average Alex and Passionate Patrick aren't just personas—they
                represent two ends of engagement we needed to serve
                simultaneously. The AI chatbox works for Alex (quick matches),
                while detailed profiles serve Patrick (deep verification). Both
                use cases informed the architecture.
              </p>
            </div>
            <div className="bg-white/70 p-4 rounded-xl">
              <h4 className="font-bold mb-2">🎨 Brand Sets Emotional Tone</h4>
              <p className="text-sm leading-relaxed">
                Establishing brand guidelines early (blues for trust, yellows
                for joy) wasn't cosmetic—it shaped every interaction. Financial
                decisions require trust, but giving should feel joyful. The
                color system balanced both emotional needs throughout the
                experience.
              </p>
            </div>
          </div>
        </CalloutBox>
      </ProjectSection>
    </ProjectPageLayout>
  );
};
